import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { H, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import * as yup from 'yup'

import axiosRequest from 'lib/axios/api-request'
import { VoucherDiscount } from 'lib/models/voucher'
import VoucherForm from '../components/voucher-form'
import { INITIAL_VOUCHER_VALUES, type VoucherFormValues } from '../interfaces'

const VOUCHER_SCHEMA = yup.object({
  productName: yup.string().required('Product is required'),
  discountType: yup.string().required('Discount type is required'),
  discountValue: yup.number().when('discountType', {
    is: (val: string) => val !== VoucherDiscount.giftCard,
    then: (schema) => schema.min(0).required('Discount value is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  code: yup.string().required('Code is required'),
  brandId: yup.string().required('Brand is required'),
  expiresAt: yup.string().required('Expiry date is required'),
  usageLimit: yup.number().integer().min(1).nullable(),
  mechanism: yup.string().nullable(),
  offerAttributes: yup.object({
    partnerName: yup.string().when('$discountType', {
      is: VoucherDiscount.giftCard,
      then: (schema) =>
        schema.required('Partner name is required for gift cards'),
      otherwise: (schema) => schema.notRequired(),
    }),
    offer: yup.string().when('$discountType', {
      is: VoucherDiscount.giftCard,
      then: (schema) =>
        schema.required('Offer description is required for gift cards'),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
})

const NewVoucher = (): React.ReactElement => {
  const history = useHistory()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (values: VoucherFormValues) => {
    setSubmitError(null)
    try {
      const attributes = {
        productName: values.productName,
        discountType: values.discountType,
        discountValue:
          values.discountType === VoucherDiscount.giftCard
            ? 0
            : Number(values.discountValue),
        code: values.code,
        brandId: values.brandId,
        expiresAt: values.expiresAt || null,
        usageLimit: values.usageLimit !== '' ? Number(values.usageLimit) : null,
        mechanism: values.mechanism || null,
        offerAttributes:
          values.discountType === VoucherDiscount.giftCard
            ? {
                partnerName: values.offerAttributes.partnerName,
                offer: values.offerAttributes.offer,
              }
            : null,
      }

      await axiosRequest({
        url: '/api/vouchers',
        method: 'POST',
        data: {
          data: { type: 'vouchers', attributes },
        },
      })

      history.push('/admin/vouchers')
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { errors?: { title?: string }[] } } })
          ?.response?.data?.errors?.[0]?.title ?? 'Failed to create voucher'
      setSubmitError(message)
    }
  }

  return (
    <Wrapper container>
      <H tag="h1" size="L" margin={[GTR.L, 0, 0]}>
        Create voucher code
      </H>
      {submitError && (
        <Wrapper
          margin={[GTR.M, 0, 0]}
          padding={GTR.M}
          style={{
            background: '#f8d7da',
            borderRadius: 4,
            border: '1px solid #f5c2c7',
          }}
        >
          {submitError}
        </Wrapper>
      )}
      <Formik
        initialValues={INITIAL_VOUCHER_VALUES}
        validationSchema={VOUCHER_SCHEMA}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <VoucherForm
              isUsed={false}
              backPath="/admin/vouchers"
              isSubmitting={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}

export default NewVoucher
